import { Shell } from "@/components/Shell";
import { BackgroundBeams } from "@/components/ui/background-beams";
import { Button } from "@/components/ui/button";
import {
	Card,
	CardContent,
	CardDescription,
	CardFooter,
	CardHeader,
	CardTitle,
} from "@/components/ui/card";
import {
	Field,
	FieldError,
	FieldGroup,
	FieldLabel,
	FieldSet,
} from "@/components/ui/field";
import { useForm } from "@/components/ui/form";
import {
	InputGroup,
	InputGroupAddon,
	InputGroupButton,
	InputGroupInput,
} from "@/components/ui/input-group";
import { authClient } from "@/lib/auth-client";
import { useMutation } from "@tanstack/react-query";
import {
	EyeIcon,
	EyeOffIcon,
	LockIcon,
	MailIcon,
	UserIcon,
} from "lucide-react";
import { AnimatePresence, motion } from "motion/react";
import { useState } from "react";
import { Link, Navigate, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import z from "zod";

const schema = z.object({
	name: z.string().min(1, "Name is required"),
	email: z.email("Invalid email address"),
	password: z
		.string("Password is required")
		.min(8, "Password must be at least 8 characters long"),
});

export function SignUp() {
	const navigate = useNavigate();
	const [showPassword, setShowPassword] = useState(false);
	const { data: session } = authClient.useSession();

	const { mutate: signUp, isPending } = useMutation({
		mutationFn: async ({
			name,
			email,
			password,
		}: {
			name: string;
			email: string;
			password: string;
		}) => {
			const result = await authClient.signUp.email({
				name,
				email,
				password,
				callbackURL: "/",
			});
			if (result.error) {
				throw new Error(result.error.message ?? "Sign up failed");
			}
			return result.data;
		},
		onSuccess: (data) => {
			if (data.user.emailVerified) {
				toast.success("Sign up successful");
			} else {
				toast.success("Verification email sent", {
					description: "Please check your email for a verification link",
				});
			}
			navigate("/", { replace: true });
		},
		onError: (error) => {
			toast.error(error.message ?? "Sign up failed");
		},
	});

	const form = useForm<z.infer<typeof schema>>({
		validators: {
			onSubmit: schema,
		},
		defaultValues: { name: "", email: "", password: "" },
		onSubmit: async ({ value }) => {
			signUp({
				name: value.name,
				email: value.email,
				password: value.password,
			});
		},
	});

	if (session) return <Navigate to="/" replace />;

	return (
		<Shell className="flex items-center justify-center h-dvh">
			<BackgroundBeams />
			<Card className="w-full max-w-md z-1 bg-card/20">
				<CardHeader>
					<CardTitle>Sign up</CardTitle>
					<CardDescription>
						Create an account with email and password
					</CardDescription>
				</CardHeader>
				<CardContent>
					<form
						onSubmit={(e) => {
							e.preventDefault();
							e.stopPropagation();
							form.handleSubmit();
						}}
						id="sign-up"
					>
						<FieldGroup>
							<FieldSet>
								<form.Field name="name">
									{(field) => (
										<Field>
											<FieldLabel htmlFor="name">Name</FieldLabel>
											<InputGroup>
												<InputGroupAddon>
													<UserIcon />
												</InputGroupAddon>
												<InputGroupInput
													id="name"
													type="text"
													placeholder="Your name"
													value={field.state.value}
													onChange={(e) => field.handleChange(e.target.value)}
													onBlur={field.handleBlur}
													required
													autoComplete="name"
													disabled={isPending}
													className="h-8"
												/>
											</InputGroup>
											<FieldError errors={field.state.meta.errors} />
										</Field>
									)}
								</form.Field>
								<form.Field name="email">
									{(field) => (
										<Field>
											<FieldLabel htmlFor="email">Email</FieldLabel>
											<InputGroup>
												<InputGroupAddon>
													<MailIcon />
												</InputGroupAddon>
												<InputGroupInput
													id="email"
													type="email"
													placeholder="example@example.com"
													value={field.state.value}
													onChange={(e) => field.handleChange(e.target.value)}
													onBlur={field.handleBlur}
													required
													autoComplete="off"
													disabled={isPending}
													className="h-8"
												/>
											</InputGroup>
											<FieldError errors={field.state.meta.errors} />
										</Field>
									)}
								</form.Field>
								<form.Field name="password">
									{(field) => (
										<Field>
											<FieldLabel htmlFor="password">Password</FieldLabel>
											<InputGroup>
												<InputGroupAddon>
													<LockIcon />
												</InputGroupAddon>
												<InputGroupInput
													id="password"
													type={showPassword ? "text" : "password"}
													placeholder={showPassword ? "•••••••••" : "Password"}
													value={field.state.value}
													onChange={(e) => field.handleChange(e.target.value)}
													onBlur={field.handleBlur}
													required
													autoComplete="new-password"
													disabled={isPending}
													className="h-8"
												/>
												<InputGroupAddon align="inline-end" className="pr-2">
													<InputGroupButton
														type="button"
														variant="ghost"
														size="icon-sm"
														onClick={() => setShowPassword(!showPassword)}
													>
														<AnimatePresence initial={false} mode="popLayout">
															<motion.span
																key={showPassword ? "eye" : "eye-off"}
																initial={{ opacity: 0, scale: 0.25, filter: "blur(4px)" }}
																animate={{ opacity: 1, scale: 1, filter: "blur(0px)" }}
																exit={{ opacity: 0, scale: 0.25, filter: "blur(4px)" }}
																transition={{ type: "spring", duration: 0.3, bounce: 0 }}
															>
																{showPassword ? <EyeIcon /> : <EyeOffIcon />}
															</motion.span>
														</AnimatePresence>
													</InputGroupButton>
												</InputGroupAddon>
											</InputGroup>
											<FieldError errors={field.state.meta.errors} />
										</Field>
									)}
								</form.Field>
							</FieldSet>
						</FieldGroup>
					</form>
				</CardContent>
				<CardFooter className="flex flex-col gap-4">
					<form.Subscribe
						selector={(state) => [
							state.isSubmitting,
							state.isValid,
							state.isDirty,
						]}
					>
						{([isSubmitting, isValid, isDirty]) => (
							<Button
								type="submit"
								form="sign-up"
								className="w-full"
								disabled={!isValid || !isDirty}
								loading={isSubmitting || isPending}
							>
								Sign up
							</Button>
						)}
					</form.Subscribe>
					<p className="text-sm text-muted-foreground">
						Already have an account?{" "}
						<Link to="/sign-in" className="text-primary hover:underline">
							Sign in
						</Link>
					</p>
				</CardFooter>
			</Card>
		</Shell>
	);
}
